import { findLocalClip, loadEditorClip } from './editorClips';
import { extractFileId, getFileMeta, isFileReference } from './fileRegistry';
import { captureVideoFrame } from './videoThumbnail';
import { stableMediaKey } from './workspaceEdit';
import { isLocalMediaUrl } from './desktopFolder';
import { findLocalDisplayUrl } from './displayUrl';

/** Addresses that only live for this session (the desktop app's addresses of local files among them). */
export const isSessionOnlyUrl = (url: string): boolean => /^(blob|data):/i.test(url) || isLocalMediaUrl(url);

export interface VideoPosterOptions {
    userId?: string;
    ownerUserId?: string;
    workspaceName?: string;
    label?: string;
    /** Whether a video that is not on this device may be read from the cloud. */
    allowDownload: boolean;
}

/** The address a stored video can be streamed from, without asking the registry to refresh it. */
export const streamableVideoUrl = async (ref: string, userId?: string, ownerUserId?: string): Promise<string | null> => {
    if (/^https?:/i.test(ref)) return ref;
    const fileId = extractFileId(ref);
    if (!fileId || !userId) return null;
    let meta = await getFileMeta(userId, fileId);
    if (!meta && ownerUserId && ownerUserId !== userId) meta = await getFileMeta(ownerUserId, fileId);
    return meta?.downloadUrl || null;
};

const frameFromBlob = async (blob: Blob): Promise<Blob> => {
    const url = URL.createObjectURL(blob);
    try {
        return await captureVideoFrame(url);
    } finally {
        URL.revokeObjectURL(url);
    }
};

/**
 * A frame of a video the workspace holds. A copy on this device is used when there is
 * one: the copy in the local folder is read through the address it is shown by, so only
 * the parts of the file a frame needs are loaded. Otherwise, once downloading is allowed,
 * the frame is read straight from the cloud copy, fetching only the parts the browser
 * needs; if the server does not share the pixels, the clip is downloaded once (and kept
 * for editing) instead.
 */
export const frameOfStoredVideo = async (url: string, options: VideoPosterOptions): Promise<Blob> => {
    if (isSessionOnlyUrl(url)) return captureVideoFrame(url);
    if (isFileReference(url) && options.workspaceName) {
        try {
            const shown = await findLocalDisplayUrl(url, { userId: options.userId, workspaceName: options.workspaceName, ownerUserId: options.ownerUserId });
            if (shown?.url) return await captureVideoFrame(shown.url);
        } catch {
            // The frame could not be read that way: the copies below are tried as before
        }
    }
    const clipOptions = {
        userId: options.userId, ownerUserId: options.ownerUserId, workspaceName: options.workspaceName,
        label: options.label, defaultMimeType: 'video/mp4',
    };
    const local = await findLocalClip(url, clipOptions);
    if (local) return frameFromBlob(local.blob);
    if (!options.allowDownload) throw new Error('Not on this device yet');
    try {
        const direct = await streamableVideoUrl(url, options.userId, options.ownerUserId);
        if (direct) return await captureVideoFrame(direct, { crossOrigin: true });
    } catch (e) {
        // A slow connection would be just as slow for the whole file: try again another time
        if ((e as { name?: string })?.name === 'TimeoutError') throw e;
        // The server did not share the pixels, or the link is stale: read the frame from a full copy instead
    }
    const clip = await loadEditorClip(url, { ...clipOptions, allowDownload: true });
    return frameFromBlob(clip.blob);
};

/**
 * Where a video's picture is filed. Media with a lasting key shares one picture across
 * the app (the editor's media list, the reference picker) and keeps it in this browser.
 * Media that only lives for this session, or is known only by a node, can change under
 * the same name, so its picture is told apart by the address and not kept.
 */
export const videoPosterKey = (url: string, fallbackId: string): { key: string; keep: boolean } => {
    const stable = isSessionOnlyUrl(url) ? null : stableMediaKey(url);
    if (stable) return { key: `ref-video-${stable}`, keep: true };
    // A blob URL names its bytes; a data URL is told apart by its length and ending
    const take = url.startsWith('blob:') ? url : `${url.length}:${url.slice(-24)}`;
    return { key: `${fallbackId}:${take}`, keep: false };
};
